import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Input, Empty } from 'antd';
import { SearchOutlined, EnterOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setMenu } from 'redux/slices/menu';
import getSystemIcons from 'helpers/getSystemIcons';

/**
 * Ctrl/Cmd+K navigation over every sidebar destination.
 *
 * Routes only, for now. Record search (an order by number, a customer by
 * email) needs a backend endpoint that does not exist yet; when it lands it
 * becomes a second result group here rather than a second UI.
 */
function flattenMenu(menuList, t) {
  const out = [];
  const push = (item, trail) => {
    if (!item?.url) return;
    out.push({
      id: `${item.id}-${item.url}`,
      url: String(item.url).replace(/^\/+/, ''),
      label: t(item.name),
      trail: trail.map((name) => t(name)),
      icon: item.icon,
    });
  };

  menuList?.forEach((item) => {
    push(item, []);
    item.submenu?.forEach((sub) => {
      push(sub, [item.name]);
      sub.children?.forEach((child) => push(child, [item.name, sub.name]));
    });
  });

  return out;
}

export default function CommandPalette({ open, onClose, menuList }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  const entries = useMemo(() => flattenMenu(menuList, t), [menuList, t]);

  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return entries.slice(0, 12);
    return entries
      .map((entry) => {
        const label = entry.label.toLowerCase();
        const url = entry.url.toLowerCase();
        // Rank exact and prefix matches above matches buried in a URL, so
        // typing "ord" puts "Orders" first rather than "Notice board".
        let score = -1;
        if (label === term) score = 0;
        else if (label.startsWith(term)) score = 1;
        else if (label.includes(term)) score = 2;
        else if (url.includes(term)) score = 3;
        else if (entry.trail.join(' ').toLowerCase().includes(term)) score = 4;
        return { entry, score };
      })
      .filter((row) => row.score >= 0)
      .sort((a, b) => a.score - b.score || a.entry.label.localeCompare(b.entry.label))
      .slice(0, 12)
      .map((row) => row.entry);
  }, [entries, query]);

  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setCursor(0);
    // The modal body mounts behind antd's open transition, so a single frame
    // is not enough — the input exists but is not focusable yet. Retry over
    // the length of that transition and stop at the first success.
    const timers = [0, 60, 140, 260].map((delay) =>
      setTimeout(() => {
        if (document.activeElement === inputRef.current?.input) return;
        inputRef.current?.focus();
      }, delay),
    );
    return () => timers.forEach(clearTimeout);
  }, [open]);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  const go = (entry) => {
    if (!entry) return;
    // Close first. Navigating opens a tab and re-renders the header subtree
    // that owns this modal; closing afterwards left the portal's DOM orphaned
    // on screen with no live listeners, so the palette stayed up and the mask
    // no longer responded to clicks.
    onClose();
    dispatch(
      setMenu({ id: entry.url, url: entry.url, name: entry.label, refetch: true }),
    );
    navigate(`/${entry.url}`);
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((c) => (results.length ? (c + 1) % results.length : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((c) =>
        results.length ? (c - 1 + results.length) % results.length : 0,
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      go(results[cursor]);
    }
  };

  return (
    <Modal
      visible={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      destroyOnClose
      width={560}
      className='ks-palette'
      bodyStyle={{ padding: 0 }}
      /* No enter/leave animation. Selecting a result navigates, which
         re-renders the whole layout while the modal is mid-exit; rc-motion
         then never receives its transition-end and the modal stays on screen
         with `visible` already false. Instant dismissal is also what you want
         from a command palette. */
      transitionName=''
      maskTransitionName=''
    >
      <div className='ks-palette__search'>
        <SearchOutlined className='ks-palette__icon' />
        <Input
          ref={inputRef}
          autoFocus
          bordered={false}
          placeholder={t('search.pages')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <kbd className='ks-palette__kbd'>esc</kbd>
      </div>

      {results.length === 0 ? (
        <div className='ks-palette__empty'>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('no.results.for', { query: query.trim() })}
          />
        </div>
      ) : (
        <ul className='ks-palette__list'>
          {results.map((entry, index) => (
            <li key={entry.id}>
              <button
                type='button'
                className={`ks-palette__row${
                  index === cursor ? ' ks-palette__row--active' : ''
                }`}
                onMouseEnter={() => setCursor(index)}
                onClick={() => go(entry)}
              >
                <span className='ks-palette__row-icon'>
                  {getSystemIcons(entry.icon)}
                </span>
                <span className='ks-palette__row-text'>
                  <span className='ks-palette__row-label'>{entry.label}</span>
                  {entry.trail.length ? (
                    <span className='ks-palette__row-trail'>
                      {entry.trail.join(' / ')}
                    </span>
                  ) : null}
                </span>
                {index === cursor ? (
                  <EnterOutlined className='ks-palette__row-enter' />
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
